import { useState } from 'react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogScrollArea,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  CheckCircle, 
  Copy, 
  Download, 
  ExternalLink, 
  Eye, 
  EyeOff,
  FileText,
  Key
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface CustomGPTSuccessIntegration {
  integration_name: string;
  auth_config: {
    api_key: string;
    response_format: 'rich' | 'simple';
    allow_filter_override?: boolean;
  };
  permissions: {
    can_read: boolean;
    can_write: boolean;
    can_search: boolean;
    can_delete: boolean;
  };
}

export interface CustomGPTSuccessModalContentProps {
  onClose: () => void;
  integration: CustomGPTSuccessIntegration;
  spaceName: string;
}

export function CustomGPTSuccessModalContent({
  onClose,
  integration,
  spaceName,
}: CustomGPTSuccessModalContentProps) {
  const { t } = useTranslation();
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState<'api_key' | 'openapi' | 'openapi_url' | 'instructions' | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'openapi' | 'instructions'>('overview');
  
  // Define baseUrl at component level
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

  const handleCopy = async (content: string, type: 'api_key' | 'openapi' | 'openapi_url' | 'instructions') => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Generate binding ID from API key for OpenAPI URL
  const getBindingId = () => {
    return integration.auth_config.api_key.replace("sk_gpt_binding-", "");
  };

  // Generate OpenAPI URL for direct import
  const getOpenAPIUrl = () => {
    const bindingId = getBindingId();
    return `${baseUrl}/api/v1/gpt-actions/integration/${bindingId}/openapi.json`;
  };

  // Generate GPT instructions
  const getGPTInstructions = () => {
    const permissionsList = getPermissionsList();
    
    return `You are an AI assistant with persistent memory capabilities powered by Memara. You can:

1. Store important information from our conversations as memories
2. Search and retrieve relevant memories when needed  
3. Maintain context across sessions using your memory system
4. Organize memories with tags and categories

Your memory capabilities include: ${permissionsList.join(', ').toLowerCase()}

IMPORTANT MEMORY USAGE GUIDELINES:
- ALWAYS search your memory FIRST before answering questions about past conversations, user preferences, or stored information
- Use memory search instead of web browsing for information about our previous interactions
- Store key insights, decisions, preferences, and important information proactively
- Reference previous conversations and learnings when relevant
- Keep track of user preferences, goals, and context across sessions

When storing memories:
- Use descriptive content that captures the essence of the information
- Add relevant tags for easy categorization (e.g., "preferences", "goals", "decisions", "facts")
- Set appropriate importance levels (1-10) based on significance
- Include context about when and why the information is important

When searching memories:
- Use semantic search queries that describe what you're looking for
- Search before making assumptions about user preferences or past conversations
- Combine multiple search results to build comprehensive context

Always be helpful and leverage your persistent memory to provide better, more contextual assistance based on our conversation history.`;
  };

  const generateOpenAPISpec = () => {
    // Use environment variable for API base URL with fallback
    
    const operations: any = {};
    
    // ✅ IMPORTANT: Each operation must have a unique operationId (OpenAI requirement)
    // Our operationIds: getIntegrationInfo, createMemory, listMemories, getMemory, deleteMemory, searchMemories
    
    // Always include integration info
    operations['/api/v1/gpt-actions/integration/info'] = {
      get: {
        operationId: 'getIntegrationInfo',
        summary: 'Get integration information',
        description: 'Retrieve details about the current GPT integration including permissions and configuration',
        'x-openai-isConsequential': false,
        responses: {
          '200': {
            description: 'Integration details',
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  properties: {
                    integration_name: { type: 'string' },
                    space_id: { type: 'string' },
                    permissions: { type: 'object' },
                    response_format: { type: 'string' }
                  }
                }
              }
            }
          }
        }
      }
    };

    // Add operations based on permissions
    if (integration.permissions.can_read || integration.permissions.can_write) {
      operations['/api/v1/gpt-actions/memories'] = {};
      
      if (integration.permissions.can_write) {
        operations['/api/v1/gpt-actions/memories'].post = {
          operationId: 'createMemory',
          summary: 'Store a new memory',
          description: 'Create and store a new memory with content, tags, and metadata',
          'x-openai-isConsequential': false,
          requestBody: {
            required: true,
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  properties: {
                    content: {
                      type: 'string',
                      description: 'The memory content to store',
                      maxLength: 50000
                    },
                    tags: {
                      type: 'array',
                      items: { type: 'string' },
                      description: 'Tags for categorization'
                    },
                    category: {
                      type: 'string',
                      description: 'Memory category'
                    },
                    importance: {
                      type: 'integer',
                      minimum: 1,
                      maximum: 10,
                      description: 'Importance level (1-10)'
                    }
                  },
                  required: ['content']
                }
              }
            }
          },
          responses: {
            '201': {
              description: 'Memory stored successfully',
              content: {
                'application/json': {
                  schema: { $ref: '#/components/schemas/MemoryResponse' }
                }
              }
            }
          }
        };
      }

      if (integration.permissions.can_read) {
        operations['/api/v1/gpt-actions/memories'].get = {
          operationId: 'listMemories',
          summary: 'List recent memories',
          description: 'Retrieve a list of the most recent memories, optionally limited by count',
          'x-openai-isConsequential': false,
          parameters: [
            {
              name: 'limit',
              in: 'query',
              description: 'Maximum number of memories to return',
              schema: {
                type: 'integer',
                default: 10,
                minimum: 1,
                maximum: 50
              }
            }
          ],
          responses: {
            '200': {
              description: 'Recent memories',
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    properties: {
                      memories: {
                        type: 'array',
                        items: { $ref: '#/components/schemas/MemoryResponse' }
                      }
                    }
                  }
                }
              }
            }
          }
        };
      }
    }

    if (integration.permissions.can_read) {
      operations['/api/v1/gpt-actions/memories/{memory_id}'] = {
        get: {
          operationId: 'getMemory',
          summary: 'Get specific memory by ID',
          description: 'Retrieve detailed information about a specific memory using its unique ID',
          'x-openai-isConsequential': false,
          parameters: [
            {
              name: 'memory_id',
              in: 'path',
              required: true,
              description: 'Memory ID',
              schema: { type: 'string' }
            }
          ],
          responses: {
            '200': {
              description: 'Memory details',
              content: {
                'application/json': {
                  schema: { $ref: '#/components/schemas/MemoryResponse' }
                }
              }
            }
          }
        }
      };
    }

    if (integration.permissions.can_delete) {
      if (!operations['/api/v1/gpt-actions/memories/{memory_id}']) {
        operations['/api/v1/gpt-actions/memories/{memory_id}'] = {};
      }
      operations['/api/v1/gpt-actions/memories/{memory_id}'].delete = {
        operationId: 'deleteMemory',
        summary: 'Delete specific memory',
        description: 'Permanently delete a memory by its unique ID. This action cannot be undone.',
        'x-openai-isConsequential': false,
        parameters: [
          {
            name: 'memory_id',
            in: 'path',
            required: true,
            description: 'Memory ID',
            schema: { type: 'string' }
          }
        ],
        responses: {
          '204': { description: 'Memory deleted successfully' }
        }
      };
    }

    if (integration.permissions.can_search) {
      operations['/api/v1/gpt-actions/search'] = {
        post: {
          operationId: 'searchMemories',
          summary: 'Search memories by query',
          description: 'Search through stored memories using semantic search to find relevant content',
          'x-openai-isConsequential': false,
          requestBody: {
            required: true,
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  properties: {
                    query: {
                      type: 'string',
                      description: 'Search query'
                    },
                    limit: {
                      type: 'integer',
                      default: 10,
                      minimum: 1,
                      maximum: 50,
                      description: 'Maximum results'
                    }
                  },
                  required: ['query']
                }
              }
            }
          },
          responses: {
            '200': {
              description: 'Search results',
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    properties: {
                      memories: {
                        type: 'array',
                        items: { $ref: '#/components/schemas/MemoryResponse' }
                      },
                      total_found: { type: 'integer' },
                      query: { type: 'string' }
                    }
                  }
                }
              }
            }
          }
        }
      };
    }

    // ✅ Validation: All operationIds are unique as required by OpenAI
    // getIntegrationInfo, storeMemory, listMemories, getMemory, deleteMemory, searchMemories
    
    return {
      openapi: '3.1.0',
      info: {
        title: `Memara Memory System - ${integration.integration_name}`,
        description: `Persistent memory operations for Custom GPT: ${integration.integration_name}`,
        version: '1.0.0',
        contact: {
          name: 'Memara Support',
          email: 'support@memara.io',
          url: `${baseUrl}/legal`
        },
        license: {
          name: 'Proprietary',
          url: `${baseUrl}/legal/terms`
        }
      },
      servers: [
        {
          url: baseUrl,
          description: 'Memara API Server'
        }
      ],
      paths: operations,
      components: {
        schemas: {
          MemoryResponse: {
            type: 'object',
            properties: {
              id: { type: 'string' },
              content: { type: 'string' },
              tags: {
                type: 'array',
                items: { type: 'string' }
              },
              category: { type: 'string' },
              importance: { type: 'integer' },
              created_at: { type: 'string' },
              space_name: { type: 'string' },
              metadata: { type: 'object' },
              ...(integration.auth_config.response_format === 'rich' && {
                relevance_score: { type: 'number' }
              })
            }
          }
        },
        securitySchemes: {
          ApiKeyAuth: {
            type: 'apiKey',
            in: 'header',
            name: 'Authorization',
            description: 'Bearer token for GPT authentication'
          }
        }
      },
      security: [
        {
          ApiKeyAuth: []
        }
      ],
      'x-privacy-policy-url': `${baseUrl}/legal/privacy`,
      'x-terms-of-service-url': `${baseUrl}/legal/terms`,
      'x-api-terms-url': `${baseUrl}/legal/api-terms`
    };
  };

  const openApiSpec = generateOpenAPISpec();
  const openApiYaml = `# OpenAPI Specification for ${integration.integration_name}
# Generated by Memara - Copy this into your Custom GPT Actions configuration

${JSON.stringify(openApiSpec, null, 2)}`;

  const downloadOpenAPISpec = () => {
    const blob = new Blob([openApiYaml], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `memara-${integration.integration_name.toLowerCase().replace(/\s+/g, '-')}-openapi.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getPermissionsList = () => {
    const permissions = [];
    if (integration.permissions.can_read) permissions.push('Read memories');
    if (integration.permissions.can_write) permissions.push('Create memories');
    if (integration.permissions.can_search) permissions.push('Search memories');
    if (integration.permissions.can_delete) permissions.push('Delete memories');
    return permissions;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
        <DialogHeader className="flex-shrink-0 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50 p-6 pr-12">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Custom GPT Integration Created!
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                <span className="font-medium">{integration.integration_name}</span> is ready to use
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 flex-shrink-0">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Key className="w-4 h-4 inline-block mr-2" />
              Setup Overview
            </button>
            <button
              onClick={() => setActiveTab('openapi')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'openapi'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4 inline-block mr-2" />
              OpenAPI Specification
            </button>
            <button
              onClick={() => setActiveTab('instructions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'instructions'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <ExternalLink className="w-4 h-4 inline-block mr-2" />
              GPT Instructions
            </button>
          </nav>
        </div>

        {/* Content */}
        <DialogScrollArea className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Integration Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Integration Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Integration Name:</span>
                    <div className="font-medium text-gray-900">{integration.integration_name}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Connected to Space:</span>
                    <div className="font-medium text-gray-900">{spaceName}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Response Format:</span>
                    <div className="font-medium text-gray-900 capitalize">{integration.auth_config.response_format}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Permissions:</span>
                    <div className="font-medium text-gray-900">{getPermissionsList().join(', ')}</div>
                  </div>
                </div>
              </div>

              {/* API Key */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-blue-900">API Key</h4>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => setShowApiKey(!showApiKey)}
                      variant="outline"
                      size="sm"
                      className="text-blue-700 border-blue-300"
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      onClick={() => handleCopy(integration.auth_config.api_key, 'api_key')}
                      variant="outline"
                      size="sm"
                      className="text-blue-700 border-blue-300"
                    >
                      {copied === 'api_key' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="bg-white border border-blue-200 rounded p-3 font-mono text-sm">
                  {showApiKey ? integration.auth_config.api_key : '••••••••••••••••••••••••••••••••••••••••••••••••••••'}
                </div>
                <p className="text-blue-700 text-xs mt-2">
                  ⚠️ Store this API key securely. It won't be shown again.
                </p>
              </div>

              {/* Next Steps */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-3">Next Steps</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-green-800">
                  <li>Go to ChatGPT and create a new Custom GPT</li>
                  <li>In the Actions section, click "Create new action"</li>
                  <li><strong>Recommended:</strong> Click "Import from URL" and use the URL from the OpenAPI tab</li>
                  <li><em>Alternative:</em> Copy the OpenAPI specification manually from the OpenAPI tab</li>
                  <li>Copy the GPT instructions from the Instructions tab into your GPT's instructions</li>
                  <li>Set Authentication to "API Key" with header "Authorization"</li>
                  <li>Use format: <code className="bg-green-100 px-1 rounded">Bearer {integration.auth_config.api_key.substring(0, 20)}...</code></li>
                                      <li>Add Privacy Policy URL: <code className="bg-green-100 px-1 rounded">{baseUrl}/legal/privacy</code></li>
                  <li>Test your GPT's memory capabilities!</li>
                </ol>
              </div>

              {/* Privacy Policy Information */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-medium text-purple-900 mb-3">📋 Required Legal Information</h4>
                <p className="text-purple-700 text-sm mb-3">
                  OpenAI requires privacy policy URLs for Custom GPT actions. Use these URLs when prompted:
                </p>
                                  <div className="space-y-2 text-sm text-purple-800">
                    <div><strong>Privacy Policy:</strong> <code className="bg-purple-100 px-1 rounded">{baseUrl}/legal/privacy</code></div>
                    <div><strong>Terms & Conditions:</strong> <code className="bg-purple-100 px-1 rounded">{baseUrl}/legal/terms</code></div>
                    <div><strong>API Terms:</strong> <code className="bg-purple-100 px-1 rounded">{baseUrl}/legal/api-terms</code></div>
                    <div><strong>Contact:</strong> <code className="bg-purple-100 px-1 rounded">legal@memara.io</code></div>
                  </div>
              </div>
            </div>
          )}

          {activeTab === 'openapi' && (
            <div className="space-y-4">
              {/* OpenAPI URL for Direct Import */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-indigo-900">📁 Import from URL (Recommended)</h4>
                    <p className="text-indigo-700 text-sm">
                      Use this URL to import the OpenAPI spec directly in ChatGPT Custom Actions
                    </p>
                  </div>
                  <Button
                    onClick={() => handleCopy(getOpenAPIUrl(), 'openapi_url')}
                    variant="outline"
                    size="sm"
                    className="text-indigo-700 border-indigo-300"
                  >
                    {copied === 'openapi_url' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied === 'openapi_url' ? t('customGPTSuccess.buttons.copied', { ns: 'integrations' }) as string : t('customGPTSuccess.buttons.copyUrl', { ns: 'integrations' }) as string}
                  </Button>
                </div>
                <div className="bg-white border border-indigo-200 rounded p-3 font-mono text-sm break-all">
                  {getOpenAPIUrl()}
                </div>
                <div className="mt-3 text-indigo-700 text-sm">
                  <strong>How to use:</strong>
                  <ol className="list-decimal list-inside mt-1 space-y-1">
                    <li>In ChatGPT Custom Actions, click "Import from URL"</li>
                    <li>Paste the URL above</li>
                    <li>The schema will be automatically imported</li>
                  </ol>
                </div>
              </div>

              {/* Manual OpenAPI Specification */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">📋 Manual Copy-Paste (Alternative)</h4>
                  <p className="text-gray-600 text-sm">
                    Copy this specification if you prefer manual configuration
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleCopy(openApiYaml, 'openapi')}
                    variant="outline"
                    size="sm"
                  >
                    {copied === 'openapi' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied === 'openapi' ? t('customGPTSuccess.buttons.copied', { ns: 'integrations' }) as string : t('customGPTSuccess.buttons.copy', { ns: 'integrations' }) as string}
                  </Button>
                  <Button
                    onClick={downloadOpenAPISpec}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-w-full">
                <pre className="text-green-400 text-xs whitespace-pre-wrap font-mono break-words">
                  {openApiYaml}
                </pre>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <div className="text-amber-600 text-sm">
                    <strong>OpenAI Configuration Requirements:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>✅ Each operation has unique operationId (verified)</li>
                      <li>Authentication Type: API Key</li>
                      <li>Header Name: Authorization</li>
                      <li>API Key Format: Bearer [your-api-key]</li>
                      <li>Test the integration after setup</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'instructions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">Custom GPT Instructions</h4>
                  <p className="text-gray-600 text-sm">
                    Copy these instructions into your Custom GPT's main instructions field
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleCopy(getGPTInstructions(), 'instructions')}
                    variant="outline"
                    size="sm"
                  >
                    {copied === 'instructions' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied === 'instructions' ? t('customGPTSuccess.buttons.copied', { ns: 'integrations' }) as string : t('customGPTSuccess.buttons.copy', { ns: 'integrations' }) as string}
                  </Button>
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-w-full">
                <pre className="text-green-400 text-xs whitespace-pre-wrap font-mono break-words">
                  {getGPTInstructions()}
                </pre>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <div className="text-blue-600 text-sm">
                    <strong>💡 Key Benefits of these Instructions:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Ensures ChatGPT searches memory BEFORE browsing the web</li>
                      <li>Encourages proactive memory storage of important information</li>
                      <li>Provides guidelines for effective memory organization</li>
                      <li>Optimizes memory search for better context retrieval</li>
                      <li>Creates consistent memory-first behavior</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <div className="text-amber-600 text-sm">
                    <strong>⚙️ Additional Configuration Tips:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Disable "Web Browsing" capability to prioritize memory search</li>
                      <li>Consider disabling "DALL-E" if not needed to focus on memory tasks</li>
                      <li>Test memory storage and retrieval after setup</li>
                      <li>Train your GPT with example conversations to optimize memory usage</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogScrollArea>

        <DialogFooter variant="scrollable" className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-gray-600">
              Need help? Check our <a href="/docs" className="text-indigo-600 hover:text-indigo-700">documentation</a>
            </div>
            <Button
              onClick={onClose}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Done
            </Button>
          </div>
        </DialogFooter>
    </div>
  );
}

interface CustomGPTSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: CustomGPTSuccessIntegration;
  spaceName: string;
}

export function CustomGPTSuccessModal({
  isOpen,
  onClose,
  integration,
  spaceName,
}: CustomGPTSuccessModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent variant="scrollable" className="max-w-5xl">
        <CustomGPTSuccessModalContent
          integration={integration}
          onClose={onClose}
          spaceName={spaceName}
        />
      </DialogContent>
    </Dialog>
  );
}
 